/**
 * Step 3 — Timesheet Settings [TYPE A — Recommended]
 *
 * Embeds SettingsPage showing ONLY:
 *   - "Submissions & Deadlines" tab (submissions-deadlines)
 *   - "Time Entry Rules" tab (general)
 *
 * Hidden tabs: working-hours (Policy Rules — Step 4), overtime, utilisation.
 * Tab is forced to "submissions-deadlines" on mount.
 *
 * The SettingsPage fixed footer is hidden via scoped DOM ref and replaced
 * by a "Save & Continue" button that programmatically clicks it so all
 * existing save logic (validation, toast, rowVersion) runs as normal.
 */

import { useRef, useEffect, useCallback } from 'react';
import { SettingsPage } from '@/components/Pages/SettingsPage';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';

interface Step3TimesheetSettingsProps {
  onNext: (status: 'completed' | 'skipped') => void;
  onBack: () => void;
}

const HIDE_TABS = ['working-hours', 'overtime', 'utilisation'];
const DEFAULT_TAB = 'submissions-deadlines';

function useHideSettingsChrome(embedRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const apply = () => {
      const container = embedRef.current;
      if (!container) return;

      // 1. Hide the PageHeader row (first child of space-y-6 root)
      const root = container.querySelector<HTMLElement>('div.space-y-6');
      if (root) {
        const firstChild = root.children[0] as HTMLElement | undefined;
        if (firstChild) firstChild.style.display = 'none';
        // Remove pb-24 reserved for the fixed footer
        root.style.paddingBottom = '0';
      }

      // 2. Hide unwanted tab triggers
      container.querySelectorAll<HTMLElement>('[role="tab"]').forEach((tab) => {
        const val = tab.getAttribute('data-value') ?? tab.getAttribute('value');
        if (val && HIDE_TABS.includes(val)) {
          tab.style.display = 'none';
        }
      });

      // 3. Force submissions-deadlines tab active if not already
      const activeTab = container.querySelector<HTMLElement>(
        `[role="tab"][data-value="${DEFAULT_TAB}"], [role="tab"][value="${DEFAULT_TAB}"]`,
      );
      if (activeTab && activeTab.getAttribute('data-state') !== 'active') {
        activeTab.click();
      }

      // 4. Hide fixed bottom footer — scoped entirely within embed container
      container.querySelectorAll<HTMLElement>('div.fixed').forEach((el) => {
        const style = window.getComputedStyle(el);
        if (style.bottom === '0px') {
          el.style.display = 'none';
          el.setAttribute('data-wizard-hidden', 'true');
        }
      });
    };

    apply();
    const t1 = setTimeout(apply, 100);
    const t2 = setTimeout(apply, 400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      // Restore the footer when leaving this step
      embedRef.current
        ?.querySelectorAll<HTMLElement>('[data-wizard-hidden="true"]')
        .forEach((el) => {
          el.style.display = '';
          el.removeAttribute('data-wizard-hidden');
        });
    };
  }, [embedRef]);
}

export function Step3TimesheetSettings({ onNext, onBack }: Step3TimesheetSettingsProps) {
  const embedRef = useRef<HTMLDivElement>(null);
  useHideSettingsChrome(embedRef);

  // Programmatically click the hidden Save button so all existing save logic
  // (validation, API call, toast, rowVersion tracking) runs exactly as normal.
  const triggerSave = useCallback(() => {
    const container = embedRef.current;
    if (!container) return;
    const saveBtn = container.querySelector<HTMLButtonElement>(
      '[data-wizard-hidden="true"] button:not([disabled]):last-of-type',
    );
    saveBtn?.click();
  }, []);

  const handleNext = useCallback(() => {
    triggerSave();
    onNext('completed');
  }, [triggerSave, onNext]);

  const handleSkip = useCallback(() => {
    onNext('skipped');
  }, [onNext]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Embedded settings panel — submissions & time entry tabs only */}
      <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-1">
        <div
          className="flex flex-col h-full min-h-0 border border-border rounded-lg bg-white overflow-hidden"
          style={{ transform: 'translateZ(0)', willChange: 'transform' }}
        >
          <div ref={embedRef} className="flex-1 min-h-0 overflow-y-auto p-4">
            <SettingsPage initialTab="submissions-deadlines" />
          </div>
        </div>
      </div>

      {/* Wizard bottom bar — replaces SettingsPage's own footer */}
      <div className="shrink-0 border-t bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-xl">
            Configure your submission deadline, grace period, and time entry rules before your
            first timesheets are submitted — they are harder to change once data exists.
          </p>
          <div className="flex items-center gap-3 shrink-0">
            <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="gap-1 text-muted-foreground"
            >
              <SkipForward className="h-4 w-4" />
              Skip for now
            </Button>
            <Button
              size="sm"
              onClick={handleNext}
              className="gap-1 bg-[#00796B] hover:bg-[#00695C] text-white"
            >
              Save & Continue
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
